import React, { useMemo } from 'react';

import { IPersonaFront } from '@api/routers/user/service';
import styled from '@emotion/styled';
import { useTranslations } from 'next-intl';
import { formatEther } from 'viem';

import { Button } from '@web/ui/components';
import { IModalProps } from '@web/ui/components/modal';

import { useDepositsOrThrow, usePersonaRewards, useUnstakeAll } from '../hooks';

import BoltIcon from './assets/bolt.svg';
import { BaseStakingModal } from './BaseStakingModal';
import { StakingAlert } from './components/StakingAlert';
import { StakingStep } from './components/StakingStep';
import { getModalStatus } from './getModalStatus';

const Action = styled(Button)`
  white-space: nowrap;
`;

const BoostAmount = styled.div`
  display: inline-flex;
  gap: ${({ theme }) => theme.spacing('0250')};
  padding-inline: ${({ theme }) => theme.spacing('0250')};
  align-items: center;
`;

const Group = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing('0250')};
`;

export interface IUnstakeModalProps extends IModalProps {
  personas: IPersonaFront[];
}

export const UnstakeModal = ({
  personas,
  onClose,
  open,
  ...props
}: IUnstakeModalProps) => {
  const t = useTranslations();
  const { depositsRecord } = useDepositsOrThrow({ enabled: open });
  const rewards = usePersonaRewards(
    personas.map((p) => p.tokenId),
    { enabled: open },
  );

  const unstakeAll = useUnstakeAll();

  const boostedCards = useMemo(() => {
    const tokenIds = personas.map((p) => p.tokenId);

    return Object.entries(depositsRecord).reduce((acc, curr) => {
      if (!tokenIds.includes(BigInt(curr[0]))) {
        return acc;
      }

      if (curr[1].bonusPerDay === 0n) {
        return acc;
      }

      return acc + 1;
    }, 0);
  }, [personas]);

  const handleClose = () => {
    unstakeAll.mutation.reset();
    onClose();
  };

  const status = getModalStatus([
    unstakeAll.mutation.status,
    unstakeAll.receipt.status,
  ]);

  return (
    <BaseStakingModal
      personas={personas}
      status={status}
      title={t('UNSTAKE_MODAL_TITLE', {
        rewards: Number(formatEther(rewards)).toFixed(2),
      })}
      completedText={t('UNSTAKE_MODAL_COMPLETED_TEXT')}
      onClose={handleClose}
      actions={[
        <Action
          label={t('UNSTAKE_MODAL_ACTION_CONFIRM')}
          variant="secondary"
          color="onSurface"
          background="surface"
          onClick={() =>
            unstakeAll.mutation.mutate({
              tokenIds: personas.map((p) => p.tokenId),
            })
          }
          fullWidth
        />,
        <Action
          label={t('UNSTAKE_MODAL_ACTION_CANCEL')}
          onClick={handleClose}
          fullWidth
        />,
      ]}
      open={open}
      {...props}
    >
      <Group>
        {boostedCards > 0 && (
          <StakingAlert>
            {t.rich('UNSTAKE_MODAL_ALERT_MESSAGE', {
              boost: () => (
                <BoostAmount>
                  <BoltIcon size="1125" />
                  {boostedCards}
                </BoostAmount>
              ),
            })}
          </StakingAlert>
        )}
        {status === 'error' && (
          <StakingAlert>{t('STAKING_ALERT_ERROR')}</StakingAlert>
        )}
      </Group>
      <Group>
        <StakingStep
          title={t('STAKING_STEP_CLAIM_TITLE')}
          description={t('STAKING_STEP_CLAIM_DESCRIPTION')}
          status={status}
          type="claim"
        />
        <StakingStep
          title={t('STAKING_STEP_UNSTAKE_TITLE')}
          description={t('STAKING_STEP_UNSTAKE_DESCRIPTION')}
          status={status}
          type="unstake"
        />
      </Group>
    </BaseStakingModal>
  );
};
